import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Clock, CheckCircle, X } from 'lucide-react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { Icon } from '../../foundation/primitives';
import { SuccessEffect, FailureEffect } from '../../effects';

const Template13Deduction = ({ hostSubmit, hostConfig, registryId = 'minigame_deduction_13', templateId = 13 }) => {
  const defaultConfig = {
    title: '推理クイズ',
    questions: [
      {
        id: 'q1',
        question: '被害者が最後に目撃されたのは何時？',
        options: ['午後8時', '午後9時', '午後10時', '午後11時'],
        correct: 1,
        hint: '目撃者の証言を思い出せ',
        explanation: '目撃者Aが午後9時に被害者を見たと証言している'
      },
      {
        id: 'q2',
        question: '犯行現場に残されていた証拠は？',
        options: ['足跡', '指紋', '髪の毛', '血痕'],
        correct: 2,
        hint: '鑑識報告書を確認',
        explanation: 'ドアノブから犯人の髪の毛が検出された'
      },
      {
        id: 'q3',
        question: '最も怪しい人物は誰？',
        options: ['執事', '庭師', '料理人', '運転手'],
        correct: 0,
        hint: 'アリバイを確認',
        explanation: '執事だけがアリバイを証明できなかった'
      }
    ],
    passingScore: 66,
    timeLimitSec: null,
    conclusion: '見事な推理だ。事件の真相に迫った。',
    successTitle: '推理成功！',
    failureTitle: '推理失敗...',
    successIcon: '🎉',
    failureIcon: '😔',
    completeButtonLabel: '完了',
    retryButtonLabel: 'やり直す',
    failSubmitButtonLabel: '未整理のまま進む',
    visualTheme: 'default'
  };

  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const next = { ...defaultConfig, ...(hostConfig ?? {}) };
    if (!Array.isArray(next.questions) || next.questions.length === 0) {
      next.questions = defaultConfig.questions;
    }
    return next;
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [gameState, setGameState] = useState('playing');
  const [timeLeft, setTimeLeft] = useState(resolvedConfig.timeLimitSec ?? null);
  const [showEffect, setShowEffect] = useState(null);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setCurrentIndex(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowResult(false);
    setShowHint(false);
    setGameState('playing');
    setTimeLeft(resolvedConfig.timeLimitSec ?? null);
    resetSubmission();
  }, [resetSubmission, resolvedConfig]);

  useEffect(() => {
    if (gameState !== 'playing' || timeLeft === null) return;
    if (timeLeft <= 0) {
      setGameState('result');
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState, timeLeft]);

  const currentQuestion = resolvedConfig.questions[currentIndex];
  const totalQuestions = resolvedConfig.questions.length;
  const correctCount = answers.filter((a, i) => a === resolvedConfig.questions[i]?.correct).length;
  const score = Math.round((correctCount / totalQuestions) * 100);
  const passed = score >= resolvedConfig.passingScore;
  const isSomaTheme = resolvedConfig.visualTheme === 'soma';

  const shellStyle = backgroundStyle ?? {
    background: isSomaTheme
      ? 'radial-gradient(ellipse at 50% 0%, rgba(57,91,99,0.42), transparent 54%), linear-gradient(180deg, #06090b 0%, #101718 52%, #050607 100%)'
      : 'var(--mg-bg)'
  };

  const somaPanelStyle = isSomaTheme
    ? {
        borderColor: 'rgba(216, 210, 191, 0.35)',
        background: 'linear-gradient(180deg, rgba(17, 24, 24, 0.94), rgba(8, 12, 13, 0.94))',
        borderRadius: 8,
        boxShadow: '0 18px 44px rgba(0, 0, 0, 0.35)'
      }
    : undefined;

  const getOptionClassName = (idx) => {
    if (!isSomaTheme) {
      return `w-full p-3 rounded-xl text-left transition-all ${
        showResult
          ? idx === currentQuestion.correct
            ? 't13-opt--correct'
            : idx === selectedOption
              ? 't13-opt--wrong'
              : 't13-opt--dim'
          : selectedOption === idx
            ? 't13-opt--selected scale-[1.02]'
            : 't13-opt--idle'
      }`;
    }

    return `w-full p-3 rounded-lg text-left transition-all ${
      showResult
        ? idx === currentQuestion.correct
          ? 'bg-cyan-900/55 border-2 border-cyan-200'
          : idx === selectedOption
            ? 'bg-red-950/55 border-2 border-red-300'
            : 'bg-stone-950/55 border border-stone-500/50 opacity-60'
        : selectedOption === idx
          ? 'bg-cyan-950/55 border-2 border-cyan-200 scale-[1.01]'
          : 'bg-stone-950/80 border border-stone-500/45 hover:border-stone-300/70'
    }`;
  };

  const getCircleClassName = (idx) => {
    const base = 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ';
    if (isSomaTheme) {
      return base + (
        showResult && idx === currentQuestion.correct
          ? 'bg-emerald-500 text-white'
          : showResult && idx === selectedOption
            ? 'bg-red-500 text-white'
            : selectedOption === idx
              ? 'bg-amber-500 text-white'
              : 'bg-slate-600 text-slate-300'
      );
    }
    return base + (
      showResult && idx === currentQuestion.correct
        ? 't13-circle--correct'
        : showResult && idx === selectedOption
          ? 't13-circle--wrong'
          : selectedOption === idx
            ? 't13-circle--selected'
            : 't13-circle--idle'
    );
  };

  const handleSelect = (optionIndex) => {
    if (showResult) return;
    setSelectedOption(optionIndex);
  };

  const handleConfirm = () => {
    if (selectedOption === null) return;

    const newAnswers = [...answers, selectedOption];
    setAnswers(newAnswers);
    setShowResult(true);

    const isAnswerCorrect = selectedOption === currentQuestion?.correct;
    setShowEffect(isAnswerCorrect ? 'correct' : 'incorrect');
  };

  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setShowResult(false);
      setShowHint(false);
    } else {
      setGameState('result');
    }
  };

  const isCorrect = showResult && selectedOption === currentQuestion?.correct;

  const reset = () => {
    setCurrentIndex(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowResult(false);
    setShowHint(false);
    setGameState('playing');
    setTimeLeft(resolvedConfig.timeLimitSec ?? null);
    setShowEffect(null);
    resetSubmission();
  };

  return (
    <div className="relative w-full h-full overflow-hidden" style={shellStyle}>
      <div className={`absolute top-0 left-0 right-0 p-3 z-10 ${isSomaTheme ? 'bg-gradient-to-b from-black/80 to-transparent' : 't13-topbar'}`}>
        <div className="flex justify-between items-center">
          <div className={`text-sm font-bold ${isSomaTheme ? 'text-white' : 't13-title'}`}>{resolvedConfig.title}</div>
          <div className="flex items-center gap-3">
            {timeLeft !== null && (
              <div className={`text-sm ${timeLeft <= 10 ? (isSomaTheme ? 'text-red-400 animate-pulse' : 't13-timer--critical animate-pulse') : (isSomaTheme ? 'text-white/70' : 't13-timer')}`}>
                <Icon icon={Clock} size={16} className="inline mr-1" />
                {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
              </div>
            )}
            <div className={`text-sm ${isSomaTheme ? 'text-white/70' : 't13-timer'}`}>
              {currentIndex + 1} / {totalQuestions}
            </div>
          </div>
        </div>
        <div className={`mt-2 h-1 rounded-full overflow-hidden ${isSomaTheme ? 'bg-gray-700' : 't13-progress-track'}`}>
          <div
            className={`h-full transition-all duration-300 ${isSomaTheme ? 'bg-cyan-200' : 't13-progress-fill'}`}
            style={{ width: `${((currentIndex + (showResult ? 1 : 0)) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {gameState === 'playing' && currentQuestion && (
        <div className="absolute inset-0 flex flex-col pt-16 pb-4 px-4">
          <div className={`backdrop-blur rounded-xl p-4 mb-4 ${isSomaTheme ? 'bg-slate-800/90 border border-slate-600' : 't13-panel'}`} style={somaPanelStyle}>
            <div className={`text-xs mb-2 ${isSomaTheme ? 'text-cyan-100' : 't13-eyebrow'}`}>問題 {currentIndex + 1}</div>
            <div className={`text-lg ${isSomaTheme ? 'text-white' : 't13-question'}`}>{currentQuestion.question}</div>
            {showHint && currentQuestion.hint && (
              <div className={`mt-3 p-2 rounded-lg ${isSomaTheme ? 'bg-cyan-950/35 border border-cyan-200/35' : 't13-hint-box'}`}>
                <div className={`text-xs ${isSomaTheme ? 'text-cyan-100' : 't13-hint-label'}`}>{isSomaTheme ? '水面の気配' : '💡 ヒント'}</div>
                <div className={`text-sm mt-1 ${isSomaTheme ? 'text-stone-100' : 't13-hint-text'}`}>{currentQuestion.hint}</div>
              </div>
            )}
          </div>

          <div className="flex-1 flex flex-col gap-2 overflow-auto">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                className={getOptionClassName(idx)}
                onClick={() => handleSelect(idx)}
                disabled={showResult}
              >
                <div className="flex items-center gap-3">
                  <div className={getCircleClassName(idx)}>
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <div className={isSomaTheme ? 'text-white' : 't13-option-label'}>{option}</div>
                  {showResult && idx === currentQuestion.correct && (
                    <span className={`ml-auto ${isSomaTheme ? 'text-emerald-400' : 't13-icon-correct'}`}>
                      <Icon icon={CheckCircle} size={20} />
                    </span>
                  )}
                  {showResult && idx === selectedOption && idx !== currentQuestion.correct && (
                    <span className={`ml-auto ${isSomaTheme ? 'text-red-400' : 't13-icon-wrong'}`}>
                      <Icon icon={X} size={20} />
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {showResult && currentQuestion.explanation && (
            <div className={`mt-3 p-3 rounded-xl ${isSomaTheme ? 'bg-slate-800/90 border border-slate-600' : 't13-panel'}`} style={somaPanelStyle}>
              <div className={`text-xs mb-1 ${isSomaTheme ? 'text-cyan-100' : 't13-explain-label'}`}>解説</div>
              <div className={`text-sm ${isSomaTheme ? 'text-white' : 't13-question'}`}>{currentQuestion.explanation}</div>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            {!showResult ? (
              <>
                {!showHint && currentQuestion.hint && (
                  <button
                    className={`flex-1 py-3 rounded-xl ${isSomaTheme ? 'bg-slate-700 text-white' : 't13-btn-secondary'}`}
                    onClick={() => setShowHint(true)}
                  >
                    ヒントを見る
                  </button>
                )}
                <button
                  className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                    selectedOption !== null
                      ? (isSomaTheme ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white' : 't13-btn-primary')
                      : (isSomaTheme ? 'bg-slate-700 text-slate-400' : 't13-btn-disabled')
                  }`}
                  onClick={handleConfirm}
                  disabled={selectedOption === null}
                >
                  決定
                </button>
              </>
            ) : (
              <button
                className={`flex-1 py-3 rounded-xl font-bold ${isSomaTheme ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white' : 't13-btn-primary'}`}
                onClick={handleNext}
              >
                {currentIndex < totalQuestions - 1 ? '次の問題へ' : '結果を見る'}
              </button>
            )}
          </div>
        </div>
      )}

      {gameState === 'result' && (
        <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: isSomaTheme ? 'rgba(1, 6, 8, 0.86)' : 'var(--mg-overlay)' }}>
          <div className="text-center p-6 max-w-md animate-fade-in" style={isSomaTheme ? somaPanelStyle : undefined}>
            <div className="text-6xl mb-4">{passed ? resolvedConfig.successIcon : resolvedConfig.failureIcon}</div>
            <h2 className={`text-2xl font-bold mb-2 ${passed ? (isSomaTheme ? 'text-emerald-400' : 't13-result-pass') : (isSomaTheme ? 'text-red-400' : 't13-result-fail')}`}>
              {passed ? resolvedConfig.successTitle : resolvedConfig.failureTitle}
            </h2>
            <div className={`text-4xl font-bold mb-2 ${isSomaTheme ? 'text-white' : 't13-score'}`}>{score}点</div>
            <div className={`mb-4 ${isSomaTheme ? 'text-slate-400' : 't13-result-detail'}`}>
              {correctCount} / {totalQuestions} 問正解
              {resolvedConfig.passingScore && (
                <span className="ml-2">(合格: {resolvedConfig.passingScore}点以上)</span>
              )}
            </div>
            {passed && resolvedConfig.conclusion && (
              <p className={`mb-6 p-3 rounded-xl ${isSomaTheme ? 'text-slate-300 bg-slate-800/50' : 't13-conclusion'}`}>{resolvedConfig.conclusion}</p>
            )}
            <div className="flex flex-col gap-2">
              {passed ? (
                <button
                  className={`font-bold py-3 px-8 rounded-xl ${isSomaTheme ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' : 't13-btn-complete'}`}
                  onClick={() => submit('success', { score, correctCount, totalQuestions, answers })}
                >
                  {resolvedConfig.completeButtonLabel}
                </button>
              ) : (
                <>
                  <button
                    className={`font-bold py-3 px-8 rounded-xl ${isSomaTheme ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-white' : 't13-btn-retry'}`}
                    onClick={reset}
                  >
                    {resolvedConfig.retryButtonLabel}
                  </button>
                  <button
                    className="ghost-btn"
                    onClick={() => submit('fail', { score, correctCount, totalQuestions, answers })}
                  >
                    {resolvedConfig.failSubmitButtonLabel}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showEffect === 'correct' && (
        <SuccessEffect type="shine" duration={600} onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'incorrect' && (
        <FailureEffect type="flash" duration={400} onComplete={() => setShowEffect(null)} />
      )}
    </div>
  );
};

export default Template13Deduction;
